import { useEffect, useState } from "react";
import { getNflPerformance } from "../../lib/api";

const Stat = ({ testId, label, value, sub }) => (
  <div data-testid={testId} className="flex flex-col gap-1 rounded-lg border border-white/10 bg-eb-surface p-3">
    <span className="font-mono text-[9px] uppercase tracking-wide text-zinc-500">{label}</span>
    <span className="font-heading text-lg text-white">{value}</span>
    {sub && <span className="font-mono text-[9px] text-zinc-600">{sub}</span>}
  </div>
);

export const NflPerformancePanel = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    getNflPerformance()
      .then(setData)
      .catch(() => setError("Couldn't load NFL forward-tracking results."));
  }, []);

  if (error) {
    return (
      <div data-testid="nfl-performance-panel-error" className="rounded-lg border border-white/10 bg-eb-surface p-4 font-mono text-xs text-zinc-500">
        {error}
      </div>
    );
  }
  if (!data) return null;

  const s = data.summary;
  const noResultsYet = s.settled_count === 0;

  return (
    <div data-testid="nfl-performance-panel" className="flex flex-col gap-3 rounded-lg border border-white/10 bg-eb-surface p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="font-heading text-lg uppercase tracking-tight text-white">Forward Tracking</h3>
          <span className="rounded-full border border-eb-accent/40 bg-eb-accent/10 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wide text-eb-accent">
            Experimental
          </span>
        </div>
        <span data-testid="nfl-performance-counts" className="font-mono text-[10px] text-zinc-500">
          {s.settled_count} settled &middot; {s.pending_count} pending
        </span>
      </div>

      {noResultsYet ? (
        <p data-testid="nfl-performance-empty-state" className="font-mono text-[11px] text-zinc-500">
          No NFL games have finished since forward tracking started -- results will appear here once the first
          frozen pregame prediction is graded against a verified final score.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2">
            <Stat testId="nfl-performance-margin-mae" label="Margin MAE" value={s.margin_mae.value ?? "\u2014"} sub={`n=${s.margin_mae.n}`} />
            <Stat testId="nfl-performance-total-mae" label="Total MAE" value={s.total_mae.value ?? "\u2014"} sub={`n=${s.total_mae.n}`} />
            <Stat
              testId="nfl-performance-moneyline-brier"
              label="Moneyline Brier"
              value={s.moneyline_decisive_brier.value ?? "\u2014"}
              sub={`decisive only, n=${s.moneyline_decisive_brier.n} \u00b7 ${s.ties.count} tie${s.ties.count === 1 ? "" : "s"} excluded`}
            />
            <Stat
              testId="nfl-performance-market-lines"
              label="Vs. Market Line"
              value={`${s.market_line_outcomes.n_with_valid_quote}/${s.settled_count}`}
              sub="had a valid pregame quote"
            />
          </div>
          {s.market_line_outcomes.n_with_valid_quote > 0 && (
            <p data-testid="nfl-performance-market-tally" className="font-mono text-[10px] text-zinc-600">
              Spread: {s.market_line_outcomes.spread.COVER || 0}-{s.market_line_outcomes.spread.NON_COVER || 0}-{s.market_line_outcomes.spread.PUSH || 0} (C-NC-P) &middot;
              Total: {s.market_line_outcomes.total.OVER || 0}-{s.market_line_outcomes.total.UNDER || 0}-{s.market_line_outcomes.total.PUSH || 0} (O-U-P)
            </p>
          )}
        </>
      )}

      {s.excluded.count > 0 && (
        <p data-testid="nfl-performance-excluded" className="font-mono text-[10px] text-zinc-600">
          {s.excluded.count} game{s.excluded.count === 1 ? "" : "s"} excluded from this record (withheld/unresolved-QB pregame state or no pregame snapshot) &mdash;
          never mixed into the settled results above.
        </p>
      )}

      <p data-testid="nfl-performance-last-graded" className="font-mono text-[9px] text-zinc-700">
        Last graded: {s.last_graded_at ? s.last_graded_at.slice(0, 16).replace("T", " ") + " UTC" : "never"}
      </p>
    </div>
  );
};
